import type { Company } from "@/services/backend/company/company";
import { usePutCompanyMutation } from "@/services/backend/company/company-service";
import { rtkErrIsValidationError } from "@/shared/app-lib/errors/validation-error";
import { RTKQueryErrorAlert } from "@/shared/components/domain/errors/rtk-query-error-alert";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { Input } from "@/shared/components/ui/input";
import { InputErrorMessages } from "@/shared/components/ui/input-error-messages";
import { Label } from "@/shared/components/ui/label";
import { useDebouncedMutation } from "@/shared/lib/debounce/debounce";
import { PersistenceStateInfo } from "@/shared/persistence-state/persistence-state-info";
import { useState } from "react";

interface CompanyDataCardProps {
  company: Company;
}

export function CompanyDataCard({ company }: CompanyDataCardProps) {
  const [request, setRequest] = useState<Company>(company);
  const [update, { isLoading, error, isSuccess, reset }] =
    usePutCompanyMutation();
  const { persistenceState } = useDebouncedMutation(
    request,
    update,
    isLoading,
    error,
    isSuccess,
    reset,
    500,
  );

  const fieldErrors = (
    rtkErrIsValidationError(error) ? error.data.errors : {}
  ) as { [key: string]: string[] };
  const errorsFor = (field: string): string[] => fieldErrors[field] ?? [];

  return (
    <Card>
      <CardHeader className="flex justify-between">
        <CardTitle>Stammdaten</CardTitle>
        <PersistenceStateInfo state={persistenceState} />
      </CardHeader>
      <CardContent className="space-y-3">
        <RTKQueryErrorAlert error={error} />
        <div className="grid gap-1">
          <Label htmlFor="name">Name</Label>
          <Input
            id="name"
            placeholder="Unternehmensname"
            value={request.name}
            onChange={(e) => setRequest({ ...request, name: e.target.value })}
          />
          <InputErrorMessages errors={errorsFor("name")} />
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="grid gap-1">
            <Label htmlFor="email">E-Mail</Label>
            <Input
              id="email"
              placeholder="labor@example.de"
              value={request.email}
              onChange={(e) =>
                setRequest({ ...request, email: e.target.value })
              }
            />
            <InputErrorMessages errors={errorsFor("email")} />
          </div>
          <div className="grid gap-1">
            <Label htmlFor="phone">Telefon</Label>
            <Input
              id="phone"
              placeholder="+49 30 440 6225"
              value={request.phone}
              onChange={(e) =>
                setRequest({ ...request, phone: e.target.value })
              }
            />
            <InputErrorMessages errors={errorsFor("phone")} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
